import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const TransactionTable = ({
  transactions,
  selectedTransactions,
  onTransactionSelect,
  onSelectAll,
  onViewDetails,
  onRetryTransaction,
  currentPage,
  setCurrentPage,
  totalPages,
  itemsPerPage,
  setItemsPerPage,
  totalItems
}) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return { icon: 'CheckCircle', color: 'text-green-600' };
      case 'pending':
        return { icon: 'Clock', color: 'text-yellow-600' };
      case 'failed':
        return { icon: 'XCircle', color: 'text-red-600' };
      default:
        return { icon: 'Circle', color: 'text-gray-400' };
    }
  };

  const truncateHash = (hash) => {
    if (!hash) return '--';
    return `${hash?.slice(0, 8)}...${hash?.slice(-8)}`;
  };

  const truncateUrl = (url, maxLength = 40) => {
    if (!url) return '--';
    if (url?.length <= maxLength) return url;
    return `${url?.slice(0, maxLength)}...`;
  };

  const formatDate = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MM/dd/yyyy HH:mm');
    } catch {
      return '--';
    }
  };

  const allSelected = transactions?.every(tx => selectedTransactions?.includes(tx?.id));
  const someSelected = transactions?.some(tx => selectedTransactions?.includes(tx?.id));

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range?.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots?.push(1, '...');
    } else {
      rangeWithDots?.push(1);
    }

    rangeWithDots?.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots?.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots?.push(totalPages);
    }

    return rangeWithDots;
  };

  if (transactions?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Transaction History</h3>
        <p className="text-muted-foreground mb-6">
          You haven't made any notarization transactions yet. Start by archiving your first webpage.
        </p>
        <Button iconName="Plus" onClick={() => window.location.href = '/webpage-notarization'}>
          Create First Archive
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Table Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAll(e?.target?.checked)}
            />
            <h3 className="text-lg font-semibold text-foreground">
              Transactions ({totalItems})
            </h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e?.target?.value))}
              className="text-sm border border-input rounded px-2 py-1 bg-background text-foreground"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 p-4"></th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Preview</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">URL & Details</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Transaction</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Date</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions?.map((transaction) => {
              const statusInfo = getStatusIcon(transaction?.status);
              
              return (
                <tr key={transaction?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="p-4">
                    <Checkbox
                      checked={selectedTransactions?.includes(transaction?.id)}
                      onChange={(e) => onTransactionSelect(transaction?.id, e?.target?.checked)}
                    />
                  </td>
                  
                  <td className="p-4">
                    <div className="w-16 h-12 bg-muted rounded border overflow-hidden">
                      {transaction?.screenshotUrl ? (
                        <img
                          src={transaction?.screenshotUrl}
                          alt="Website preview"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="Image" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="space-y-1">
                      <p className="font-medium text-foreground text-sm">
                        {transaction?.title || 'Untitled'}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {truncateUrl(transaction?.originalUrl)}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <span>{transaction?.fileSize || '--'}</span>
                        {transaction?.ipfsHash && (
                          <>
                            <span>•</span>
                            <span className="font-mono">{truncateHash(transaction?.ipfsHash)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name={statusInfo?.icon} size={16} className={statusInfo?.color} />
                      <span className={`text-sm font-medium capitalize ${statusInfo?.color}`}>
                        {transaction?.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {transaction?.verificationStatus === 'verified' && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Shield" size={12} className="text-green-600" />
                          <span className="text-green-600">Verified</span>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="space-y-1">
                      <p className="text-xs font-mono text-foreground">
                        {truncateHash(transaction?.transactionHash)}
                      </p>
                      {transaction?.blockNumber && (
                        <p className="text-xs text-muted-foreground">
                          Block #{transaction?.blockNumber?.toLocaleString()}
                        </p>
                      )}
                      {transaction?.gasUsed && (
                        <p className="text-xs text-muted-foreground">
                          {transaction?.gasUsed?.toLocaleString()} gas
                        </p>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <p className="text-sm text-foreground">
                      {formatDate(transaction?.timestamp)}
                    </p>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails(transaction)}
                      >
                        View
                      </Button>
                      
                      {transaction?.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="RefreshCw"
                          onClick={() => onRetryTransaction(transaction)}
                        >
                          Retry
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="ExternalLink"
                        onClick={() => window.open(transaction?.originalUrl, '_blank')}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {getPaginationRange()?.map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
                  ) : (
                    <Button
                      variant={currentPage === page ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;